import { useCallback, useEffect, useState } from 'react'
import type { AudioNote } from '@highlight/common'
import { useSetAtom } from 'jotai'
import { Check, Copy, Link2, Loader2, MessageSquare, NotebookPen, Trash2 } from 'lucide-react'
import { Button, Menu, Logo, Tooltip } from '@highlight/ui'
import { ShareToSlackDialog, useCanShareToSlack } from '@renderer/features/share-to-slack'
import { formatTitle } from '@renderer/utils/conversations/conversations'
import { useIsAudioNoteSaving, useIsAudioNoteStarting } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useMeetingNoteShare } from '../../../../hooks/use-meeting-note-share'
import { FLOATING_FILL_CLASS } from '@renderer/components/floating-surface'
import { SidepanelAudioPill } from '../../../components/sidepanel/nested/audio-pill'
import { useFloatingBarCopy } from '../../../atoms'
import { meetingDeletePendingAtom } from '../atoms'
import { useMeetingViewModel, type MeetingViewContext, type MeetingViewModel } from '../use-meeting-view-model'
import { meetingNotepadKey } from '../notepad-key'
import { useMeetingNotepadToggle } from '../use-meeting-notepad'
import { FloatingBarRegionSwap } from '../../action-view/nested/floating-controls'
import { useSidepanelSurface } from '../../../profile/use-sidepanel-profile'
import { useIsSummaryDirty } from '@renderer/hooks/audio/summary-draft'
import { SummarySaveControls } from './summary-editor'

interface MeetingViewFloatingBarSlotProps {
  context: MeetingViewContext
}

/**
 * Floating-bar slot for the Meeting View, mounted by `SidepanelFloatingBar`
 * while a meeting is the top view. Resolves its own model the same way
 * `ActionViewFloatingBarSlot` does — the model derives from shared queries, so
 * the second call is cheap.
 *
 * Owns both clusters, including the live audio pill, so the row splits once
 * here rather than leaving two groups to compete for the same space. The
 * `tabs` layout has no leading cluster: its live header carries the recording
 * controls and its Notes tab the notepad.
 *
 * The floating bar mounts this outside `MeetingView`, so it reads the
 * `meetingView` surface itself.
 */
export function MeetingViewFloatingBarSlot({ context }: MeetingViewFloatingBarSlotProps) {
  const model = useMeetingViewModel(context)
  const hasLeadingControls = useSidepanelSurface('meetingView') !== 'tabs'
  const isLive = model.variant === 'live'
  const audioNote = model.audioNote ?? undefined
  const setDeletePending = useSetAtom(meetingDeletePendingAtom)
  const handleDelete = useCallback(() => setDeletePending(true), [setDeletePending])

  // Unsent summary edits replace the whole row, so the save is the only control until it resolves.
  const summaryNoteId = model.audioNoteId ?? null
  const savedSummary = audioNote?.summary ?? ''
  const isSummaryDirty = useIsSummaryDirty(summaryNoteId, savedSummary)

  const controlRow = (
    // flex-1, not just justify-between: the bar's stretch wrapper is a flex
    // row, so without it this shrinks to its content and the two clusters sit
    // side by side in the middle instead of at the row's two edges.
    <div
      className={
        hasLeadingControls
          ? 'flex min-w-0 flex-1 items-center justify-between gap-2'
          : 'flex min-w-0 flex-1 items-center justify-end gap-2'
      }
    >
      {hasLeadingControls && <MeetingViewLeadingControls model={model} />}
      {/* Everything you do *to* a finished meeting groups at the right, ending
          on Share. Deleting mid-meeting is what Stop (or the live header's
          Discard) is for, so none of these show while it's still running.

          No `layout` here, unlike the leading cluster: this one is pinned to
          the row's trailing edge, which nothing entering at the leading edge
          moves. Giving it a slide would animate it to the spot it is already
          in. */}
      <div data-tour-section="meeting-actions" className="flex shrink-0 items-center gap-2 empty:hidden">
        {/* Outside the finished-meeting gate: a live transcript and an
            upcoming brief are both worth copying, and the claim already says
            whether there is anything to copy. */}
        <Tooltip.Provider>
          <CopyMeetingButton />
        </Tooltip.Provider>
        {!isLive && audioNote && (
          <Tooltip.Provider>
            <Tooltip>
              <Tooltip.Trigger
                render={
                  <Button
                    variant="outline"
                    shape="square"
                    aria-label="Delete meeting"
                    onClick={handleDelete}
                    className={FLOATING_FILL_CLASS}
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                }
              />
              <Tooltip.Content>Delete meeting</Tooltip.Content>
            </Tooltip>
            <ShareMeetingButton audioNote={audioNote} />
          </Tooltip.Provider>
        )}
      </div>
    </div>
  )

  return (
    <FloatingBarRegionSwap regionKey={isSummaryDirty && summaryNoteId ? 'summary-save' : 'controls'}>
      {isSummaryDirty && summaryNoteId ? (
        <SummarySaveControls
          audioNoteId={summaryNoteId}
          savedText={savedSummary}
          isPublished={Boolean(audioNote?.shareLink)}
        />
      ) : (
        controlRow
      )}
    </FloatingBarRegionSwap>
  )
}

interface MeetingViewLeadingControlsProps {
  model: MeetingViewModel
}

/**
 * What you write with leads the row, on both variants (Figma 7976:28032): the
 * notepad is the one control you reach for while reading, so it holds the same
 * spot whether or not the meeting is still running. Live recording controls
 * sit ahead of it, the same place they hold on Home and in the Action View
 * (Figma 7969:27865). A running meeting is one cluster wherever you are.
 *
 * One button, two directions: it shows where it takes you, so while the
 * notepad holds the footer it offers the chat back.
 */
function MeetingViewLeadingControls({ model }: MeetingViewLeadingControlsProps) {
  const isLive = model.variant === 'live'
  const { isOpen: isNotepadOpen, toggle: toggleNotepad } = useMeetingNotepadToggle(meetingNotepadKey(model), {
    audioSessionId: model.audioSessionId ?? undefined,
    audioNoteId: model.audioNoteId ?? undefined,
  })
  // The pill's two bookend states (see `SidepanelAudioPill`): starting, with no
  // recording to take notes against yet, and saving, with one on its way out.
  // The pill collapses to a spinner for both, and the notepad goes with it.
  //
  // Gated on `isLive`, because the status is the recorder's singleton rather
  // than this meeting's: without it, starting a recording would blank the
  // notepad button on whatever past meeting the user happened to be reading.
  const isNotesStarting = useIsAudioNoteStarting()
  const isNotesSaving = useIsAudioNoteSaving()
  const isNotesSettling = isLive && (isNotesStarting || isNotesSaving)

  return (
    <div className="flex shrink-0 items-center gap-2 empty:hidden">
      {isLive && <SidepanelAudioPill />}
      {/* Nothing to take notes on until the meeting starts: an upcoming one
          that hasn't begun offers the brief, not a notepad, and neither does
          one still spinning up. It comes back the moment there's a recording
          and stays afterwards, so notes written during the meeting are still
          reachable from the row.

          An open notepad always keeps its button, whatever the recorder is
          doing: this toggle is the only thing that clears the footer claim
          (`useMeetingNotepadClaim` deliberately leaves the mode alone on
          unmount), so hiding it mid-save would strand the notes over the
          chat input with no way back. */}
      {!model.isUpcomingWithoutAudioNote && (isNotepadOpen || !isNotesSettling) && (
        // The span carries the meeting tour's anchor. The button component
        // doesn't spread unknown props. A real box (not `contents`) so the
        // tour's spotlight can measure it.
        <span data-tour-section="meeting-notepad-toggle" className="flex shrink-0">
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button
                  variant="outline"
                  shape="square"
                  className={FLOATING_FILL_CLASS}
                  aria-label={isNotepadOpen ? 'Close notepad' : 'Notepad'}
                  aria-pressed={isNotepadOpen}
                  onClick={toggleNotepad}
                >
                  {isNotepadOpen ? <MessageSquare aria-hidden="true" /> : <NotebookPen aria-hidden="true" />}
                </Button>
              }
            />
            <Tooltip.Content>{isNotepadOpen ? 'Chat' : 'Notepad'}</Tooltip.Content>
          </Tooltip>
        </span>
      )}
    </div>
  )
}

/**
 * Copies whatever the open tab holds — the brief, the transcript or the
 * summary — from the claim that tab's panel publishes. One control instead of
 * a button per section plus a fourth here that only ever meant the summary.
 * Absent while nothing offers one, so it never copies an empty clipboard.
 */
function CopyMeetingButton() {
  const slot = useFloatingBarCopy()
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])
  // The claim changes with the tab; a "Copied!" left over from the last one
  // would be claiming something about this one.
  useEffect(() => setCopied(false), [slot?.label])

  if (!slot) {
    return null
  }

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            variant="outline"
            shape="square"
            aria-label={slot.label}
            onClick={() => {
              slot.copy()
              setCopied(true)
            }}
            className={FLOATING_FILL_CLASS}
          >
            {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          </Button>
        }
      />
      <Tooltip.Content>{copied ? 'Copied!' : slot.label}</Tooltip.Content>
    </Tooltip>
  )
}

function ShareMeetingButton({ audioNote }: { audioNote: AudioNote }) {
  const canShareToSlack = useCanShareToSlack()
  return canShareToSlack ? <ShareMeetingMenu audioNote={audioNote} /> : <CopyMeetingLinkButton audioNote={audioNote} />
}

function CopyMeetingLinkButton({ audioNote }: { audioNote: AudioNote }) {
  const { handleShare, isLoading, copied, error } = useMeetingNoteShare(audioNote)

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            onClick={() => void handleShare()}
            variant="primary"
            tone={error ? 'danger' : 'neutral'}
            className="shrink-0"
            disabled={isLoading}
            loading={isLoading}
            left={copied ? <Check aria-hidden="true" /> : <Link2 aria-hidden="true" />}
          >
            {error ? 'Retry' : copied ? 'Copied' : 'Share'}
          </Button>
        }
      />
      <Tooltip.Content>{error ?? (copied ? 'Copied!' : 'Copy share link')}</Tooltip.Content>
    </Tooltip>
  )
}

/** Share as a menu once there's somewhere besides the clipboard to send the meeting. */
function ShareMeetingMenu({ audioNote }: { audioNote: AudioNote }) {
  const { handleShare, isLoading, copied, error } = useMeetingNoteShare(audioNote)
  const [isSlackDialogOpen, setIsSlackDialogOpen] = useState(false)
  const icon = isLoading ? (
    <Loader2 aria-hidden="true" className="size-4 animate-spin" />
  ) : copied ? (
    <Check aria-hidden="true" className="size-4" />
  ) : (
    <Link2 aria-hidden="true" className="size-4" />
  )

  return (
    <>
      <Menu>
        <Tooltip disabled={!error && !copied}>
          <Tooltip.Trigger
            render={
              <Menu.Trigger
                variant="primary"
                tone={error ? 'danger' : 'neutral'}
                className="shrink-0"
                disabled={isLoading}
                aria-busy={isLoading || undefined}
              >
                {icon}
                {error ? 'Retry' : copied ? 'Copied' : 'Share'}
              </Menu.Trigger>
            }
          />
          <Tooltip.Content>{error ?? 'Copied!'}</Tooltip.Content>
        </Tooltip>
        <Menu.Content side="top" align="end">
          <Menu.Item onClick={() => void handleShare()}>
            <Link2 aria-hidden="true" />
            Copy link
          </Menu.Item>
          <Menu.Item onClick={() => setIsSlackDialogOpen(true)}>
            <Logo name="slack" size={16} />
            Share to Slack
          </Menu.Item>
        </Menu.Content>
      </Menu>
      <ShareToSlackDialog
        open={isSlackDialogOpen}
        onOpenChange={setIsSlackDialogOpen}
        target={{ kind: 'meeting', audioNote }}
        title={formatTitle(audioNote.title)}
      />
    </>
  )
}
